'use client';

import { Portal } from '@common/components';
import { SvgDelete } from '@common/icon';
import { cn } from '@core/utils';
import { AnimatePresence, motion } from 'framer-motion';

import { SELECTOR } from '@/constants';

interface SessionPortalProps {
  renderTo: string | (() => HTMLElement | null);
  title?: string;
  onClose?: () => void;
  children: React.ReactNode;
  showHeader?: boolean;
  animationDuration?: number;
  className?: string;
  wrapperClassName?: string;
  stickyFooter?: React.ReactNode;
}

export const SessionPortal = ({
  renderTo,
  title,
  onClose,
  children,
  showHeader = true,
  animationDuration = 0.1,
  className,
  wrapperClassName,
  stickyFooter,
}: SessionPortalProps) => {
  const renderTarget =
    typeof renderTo === 'string'
      ? () => document.getElementById(renderTo)
      : renderTo;

  return (
    <Portal renderTo={renderTarget}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: animationDuration }}
        className={cn(
          'absolute left-0 top-0 z-10 flex size-full flex-col bg-Gray-white',
          wrapperClassName,
        )}
      >
        {showHeader ? (
          <div
            className={cn(
              'sticky top-0 box-border flex min-h-56 w-full items-center justify-between bg-Gray-white px-20 text-center',
              'z-10 break-all font-600 text-Gray-900 typo-body3',
            )}
          >
            <div />
            {title}
            {onClose ? (
              <SvgDelete
                role="button"
                className="size-20"
                onClick={onClose}
                aria-label="닫기"
              />
            ) : null}
          </div>
        ) : null}

        <motion.div
          initial={{ y: 5, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 5, opacity: 0 }}
          transition={{ duration: animationDuration }}
          className="flex w-full flex-1 overflow-y-auto hide-scrollbar"
          id={SELECTOR.SESSION_PORTAL_CONTENT}
        >
          <AnimatePresence mode="wait">
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: animationDuration }}
              className={cn('h-fit w-full', className)}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </motion.div>

        <div className="flex w-full">{stickyFooter}</div>
      </motion.div>
    </Portal>
  );
};
